import React, { useState, useEffect, useCallback } from 'react';
import { Lightbulb, AlertTriangle, Info, Zap, RefreshCw, AlertCircle, Clock } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import { getInsightReport } from '../../services/api';

interface InsightCardProps {
    projectId: string;
}

interface Insight {
    title: string;
    description: string;
    severity: 'info' | 'warning' | 'opportunity';
}

interface Prediction {
    metric: string;
    forecast: string;
    confidence: number;
}

interface Recommendation {
    priority: number;
    action: string;
    rationale: string;
}

interface InsightReport {
    headline: string;
    insights: Insight[];
    predictions: Prediction[];
    recommendations: Recommendation[];
    generated_at: string;
}

function getSeverityIcon(severity: string) {
    switch (severity) {
        case 'warning': return <AlertTriangle size={16} style={{ color: 'var(--warning)' }} />;
        case 'opportunity': return <Zap size={16} style={{ color: 'var(--success)' }} />;
        default: return <Info size={16} style={{ color: 'var(--info)' }} />;
    }
}

function getSeverityVariant(severity: string): 'info' | 'warning' | 'success' {
    switch (severity) {
        case 'warning': return 'warning';
        case 'opportunity': return 'success';
        default: return 'info';
    }
}

function timeAgo(iso: string): string {
    try {
        const diff = Date.now() - new Date(iso).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins}m ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    } catch {
        return '';
    }
}

const InsightCard: React.FC<InsightCardProps> = ({ projectId }) => {
    const [report, setReport] = useState<InsightReport | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchInsights = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getInsightReport(projectId);
            setReport(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to generate insights');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchInsights();
    }, [fetchInsights]);

    return (
        <div className="insight-section">
            {/* Loading */}
            {loading && (
                <GlassCard padding="lg">
                    <div className="trend-loading">
                        <RefreshCw size={24} className="trend-spinner" style={{ color: 'var(--primary-500)' }} />
                        <p style={{ color: 'var(--text-secondary)', margin: '12px 0 0' }}>
                            Generating insights with AI...
                        </p>
                    </div>
                </GlassCard>
            )}

            {/* Error */}
            {error && !loading && (
                <GlassCard padding="md">
                    <div className="trend-error">
                        <AlertCircle size={20} style={{ color: 'var(--danger)' }} />
                        <span style={{ color: 'var(--danger)' }}>{error}</span>
                        <GlassButton variant="secondary" size="sm" onClick={fetchInsights}>
                            Retry
                        </GlassButton>
                    </div>
                </GlassCard>
            )}

            {report && !loading && (
                <>
                    {/* Headline */}
                    <GlassCard padding="md" className="insight-headline-card">
                        <div className="insight-headline-row">
                            <Lightbulb size={22} style={{ color: 'var(--primary-500)', flexShrink: 0 }} />
                            <div>
                                <h3 className="insight-headline">{report.headline}</h3>
                                <div className="insight-timestamp">
                                    <Clock size={12} />
                                    <span>Generated {timeAgo(report.generated_at)}</span>
                                </div>
                            </div>
                        </div>
                    </GlassCard>

                    {/* Insights List */}
                    {report.insights && report.insights.length > 0 && (
                        <div className="insight-cards-grid">
                            {report.insights.map((insight, idx) => (
                                <GlassCard key={idx} padding="md" className="insight-card">
                                    <div className="insight-card-header">
                                        {getSeverityIcon(insight.severity)}
                                        <h4 className="insight-card-title">{insight.title}</h4>
                                        <GlassBadge variant={getSeverityVariant(insight.severity)} size="sm">
                                            {insight.severity}
                                        </GlassBadge>
                                    </div>
                                    <p className="insight-card-desc">{insight.description}</p>
                                </GlassCard>
                            ))}
                        </div>
                    )}

                    {/* Predictions */}
                    {report.predictions && report.predictions.length > 0 && (
                        <GlassCard padding="md" className="insight-predictions-card">
                            <h4 className="insight-section-title">Predictions</h4>
                            <div className="insight-predictions-list">
                                {report.predictions.map((pred, idx) => (
                                    <div key={idx} className="insight-prediction-item">
                                        <div className="insight-prediction-metric">{pred.metric}</div>
                                        <div className="insight-prediction-forecast">{pred.forecast}</div>
                                        <GlassBadge
                                            variant={pred.confidence >= 0.7 ? 'success' : pred.confidence >= 0.4 ? 'warning' : 'danger'}
                                            size="sm"
                                        >
                                            {Math.round(pred.confidence * 100)}%
                                        </GlassBadge>
                                    </div>
                                ))}
                            </div>
                        </GlassCard>
                    )}

                    {/* Recommendations */}
                    {report.recommendations && report.recommendations.length > 0 && (
                        <GlassCard padding="md" className="insight-recommendations-card">
                            <h4 className="insight-section-title">Recommendations</h4>
                            <ol className="insight-recommendations-list">
                                {report.recommendations
                                    .sort((a, b) => a.priority - b.priority)
                                    .map((rec, idx) => (
                                        <li key={idx} className="insight-recommendation-item">
                                            <div className="insight-recommendation-row">
                                                <span className="insight-recommendation-priority">#{rec.priority}</span>
                                                <div>
                                                    <div className="insight-recommendation-action">{rec.action}</div>
                                                    <div className="insight-recommendation-rationale">{rec.rationale}</div>
                                                </div>
                                            </div>
                                        </li>
                                    ))
                                }
                            </ol>
                        </GlassCard>
                    )}
                </>
            )}
        </div>
    );
};

export default InsightCard;
